import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { resetPasswordSchema, type ResetValues } from "@/lib/schemas";
import { api, formatApiError } from "@/lib/api";
import AuthShell from "@/layouts/AuthShell";
import { Button, Input, Label, toast } from "@/components/ds";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [error, setError] = useState("");
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ResetValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: "", confirm: "" },
  });

  const onSubmit = async (values: ResetValues) => {
    setError("");
    try {
      await api.resetPassword(token, values.password);
      toast.success("Password reset — please sign in");
      navigate("/login");
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(msg);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Reset link invalid" subtitle="This link is missing or malformed.">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="reset-invalid">
          Please request a new reset link.
        </div>
        <p className="mt-4 text-center text-sm text-slate-500">
          <Link to="/forgot-password" className="text-blue-600 hover:text-blue-700">Request a link</Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" data-testid="reset-form">
        <div>
          <Label htmlFor="password">New password</Label>
          <Input id="password" type="password" data-testid="password-input" {...register("password")} />
          {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
        </div>
        <div>
          <Label htmlFor="confirm">Confirm password</Label>
          <Input id="confirm" type="password" data-testid="confirm-input" {...register("confirm")} />
          {errors.confirm && <p className="mt-1 text-xs text-red-600">{errors.confirm.message}</p>}
        </div>
        {error && <p className="text-sm font-medium text-red-600" data-testid="reset-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="reset-submit">
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />} Reset password
        </Button>
      </form>
    </AuthShell>
  );
}
